import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import { change } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';

import { updateOrderLines } from 'redux/modules/ordersLines';

import getDocumentFieldNames from 'utils/getDocumentFieldNames';
import getCurrentAppDocuments from 'utils/getCurrentAppDocuments';

import documents from 'config/documents';

import ScheduleCalendarTable from './ScheduleCalendarTable';
import ScheduleCalendarNoDrivers from './ScheduleCalendarNoDrivers';

const styles = theme => ({
  root: {
    position: 'relative',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    overflow: 'hidden',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      backgroundColor: theme.palette.primary.A100,
      zIndex: 2,
      top: 0,
      left: 95,
      bottom: 0,
      width: 1,
      [theme.breakpoints.down('xs')]: {
        left: 38,
      },
    },
  },
  rootVariantSingleDate: {
    '&:before': {
      left: 199,
    },
  },
  tableContainer: {
    marginTop: 0,
    marginLeft: 95,
    overflowX: 'auto',
    overflowY: 'hidden',
    [theme.breakpoints.down('xs')]: {
      marginLeft: 38,
    },
  },
  tableContainerOrderLinesOpen: {
    paddingBottom: 250, // add class on order lines open
  },
  tableContainerVariantSingleDate: {
    marginLeft: 199,
  },
});

class ScheduleCalendar extends Component {
  constructor(props) {
    super(props);

    this.state = {
      orderLinesOpenPadding: 0,
      disabledDrag: false,
      signDialogOpen: false,
      signOrders: [],
      printFormValues: null,
    };

    this.onOrderLinesUpdate = this.onOrderLinesUpdate.bind(this);
    this.onItemDrag = this.onItemDrag.bind(this);
    this.onOrderLinesToggle = this.onOrderLinesToggle.bind(this);
    this.onDisableDrag = this.onDisableDrag.bind(this);
    this.onEnableDrag = this.onEnableDrag.bind(this);
    this.onSign = this.onSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
  }

  onOrderLinesUpdate(values) {
    const { onOrderLinesUpdate } = this.props;
    new Promise((resolve, reject) =>
      this.props.actions.updateOrderLines({
        values,
        triggerLoading: true,
        resolve,
        reject,
      })).then(() => {
      onOrderLinesUpdate();
    },
    () => {
      onOrderLinesUpdate();
    });
  }

  onOrderLinesToggle({ orderLinesOpen, orderLinesLength, orderLinesOffsetBottom }) {
    if (orderLinesOpen) {
      const dropdownHeight = Math.min(65 * orderLinesLength, 200);
      const orderLinesOpenPadding = (dropdownHeight
      - (this.tableContainerRef.getBoundingClientRect().bottom
        - orderLinesOffsetBottom)) + 20;
      this.setState({
        orderLinesOpenPadding: orderLinesOpenPadding > 0 ? orderLinesOpenPadding : 0,
      });
    } else {
      this.setState({
        orderLinesOpenPadding: 0,
      });
    }
  }

  onItemDrag({ y }) {
    const containerOffset = this.tableContainerRef.getBoundingClientRect().top;
    const itemOffset = containerOffset + y;
    const windowScrollTop = window.scrollY !== undefined ? window.scrollY : window.scrollTop;
    if (window.innerHeight - itemOffset < 50) {
      window.scrollTo(0, windowScrollTop + 50);
    } else if (itemOffset - windowScrollTop < containerOffset) {
      window.scrollTo(0, windowScrollTop - 50);
    }
  }

  onDisableDrag() {
    this.setState({ disabledDrag: true });
  }

  onEnableDrag() {
    this.setState({ disabledDrag: false });
  }

  onSign(filters) {
    const { orders } = this.props;
    const currentAppDocuments = getCurrentAppDocuments(documents, this.props.appInfo);
    const filteredOrders = orders.reduce((accumulator, currentOrder) => {
      if (currentOrder.Patient['Record Number'] === filters['Record Number']) {
        const filteredOrderLines = currentOrder['Order Line'].filter(orderLine => 
          orderLine.Driver.ID === filters['Driver ID']
          && orderLine['Date of Delivery'] === filters['Date of Delivery']).map(item => ({ ID: item.ID }));
        if (filteredOrderLines.length > 0) {
          return [
            ...accumulator,
            {
              ID: currentOrder.ID,
              'Order Line': filteredOrderLines,
            },
          ];
        }
        return accumulator;
      }
      return accumulator;
    }, []);
    
    if (filteredOrders.length > 0) { 
      const deliveryTicketsFields = filteredOrders.reduce((accumulator, currentOrder) => {
        return [
          ...accumulator,
          ...getDocumentFieldNames(currentOrder['Order Line'],
            currentAppDocuments.filter(item => ['Delivery Ticket'].indexOf(item.Type) !== -1)),
        ];
      }, []);
      this.onDisableDrag();
      this.setState({
        signDialogOpen: true,
        signOrders: filteredOrders,
        printFormValues: deliveryTicketsFields.reduce((accumulator, currentValue) => ({
          ...accumulator,
          [currentValue]: true,
        }), {}),
      });
    }
  }

  onSignDialogClose() {
    this.setState({
      signDialogOpen: false,
      signOrders: [],
    });
    this.onEnableDrag();
  }

  render() {
    const {
      items,
      variant,
      loading,
      filteredDrivers,
      disabled,
      onHorizontalScroll,
      business,
      actions,
      onSign,
      orders,
      classes,
    } = this.props;
    const {
      orderLinesOpenPadding,
      disabledDrag,
      signDialogOpen,
      signOrders,
      printFormValues,
    } = this.state;
    return (
      <Paper
        className={classNames(classes.root, {
          [classes.rootVariantSingleDate]: variant === 'single-date',
        })}
      >
        {filteredDrivers.length === 0 ? (
          <ScheduleCalendarNoDrivers />
        ) : (
          <div
            className={classNames(classes.tableContainer, {
              [classes.tableContainerVariantSingleDate]: variant === 'single-date',
            })}
            style={{
              paddingBottom: `${orderLinesOpenPadding}px`,
            }}
            onScroll={onHorizontalScroll}
            ref={(tableContainerRef) => { this.tableContainerRef = tableContainerRef; }}
          >
            <ScheduleCalendarTable
              variant={variant}
              items={items}
              loading={loading}
              disabled={disabled || disabledDrag}
              onOrderLinesUpdate={this.onOrderLinesUpdate}
              onItemDrag={this.onItemDrag}
              onOrderLinesToggle={this.onOrderLinesToggle}
              onRowHeadClick={(filter, value) => {
                if (filter === 'Driver') {
                  actions.change('schedule-ordersFilter', 'Driver', [value]);
                } else {
                  actions.change('schedule-ordersFilter', 'Date of Delivery From', value);
                  actions.change('schedule-ordersFilter', 'Date of Delivery To', value);
                }
              }}
              onDisableDrag={this.onDisableDrag}
              onEnableDrag={this.onEnableDrag}
              onSign={this.onSign}
              business={business}
            />
            <DocumentsSignDialog
              open={signDialogOpen}
              onClose={this.onSignDialogClose}
              printFormValues={printFormValues}
              variant="order"
              ID={signOrders && signOrders[0] ? signOrders[0].ID : null}
              orders={orders.reduce((accumulator, currentOrder) => {
                const currentSignOrder = signOrders.find(item => item.ID === currentOrder.ID);
                if (currentSignOrder) {
                  return [
                    ...accumulator,
                    {
                      ...currentOrder,
                      'Order Line':  currentOrder['Order Line'].filter(item =>
                        currentSignOrder['Order Line'].find(orderLine => orderLine.ID === item.ID)),
                    },
                  ];
                }
                return accumulator;
              }, [])}
              initialValues={(signOrders && signOrders.length > 0) ? {
                'Sign Order': signOrders.map(item => ({
                  value: item.ID,
                  label: item.ID,
                })),
              } : null}
            />
          </div>
        )}
      </Paper>
    );
  }
}

ScheduleCalendar.propTypes = {
  variant: PropTypes.oneOf(['single-date', 'date-range']).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  filteredDrivers: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  actions: PropTypes.shape({
    updateOrderLines: PropTypes.func.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onHorizontalScroll: PropTypes.func,
  onOrderLinesUpdate: PropTypes.func,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  onSign: PropTypes.func,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    rootVariantSingleDate: PropTypes.string.isRequired,
    tableContainer: PropTypes.string.isRequired,
    tableContainerVariantSingleDate: PropTypes.string.isRequired,
  }).isRequired,
};

ScheduleCalendar.defaultProps = {
  filteredDrivers: [],
  loading: false,
  disabled: false,
  onHorizontalScroll: () => {},
  onOrderLinesUpdate: () => {},
  business: {},
  onSign: () => {},
};

const mapStateToProps = store => ({
  appInfo: store.appInfo,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ updateOrderLines, change }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(ScheduleCalendar),
);



// WEBPACK FOOTER //
// ./src/components/schedule/ScheduleCalendar/ScheduleCalendar.js